import type { ReactNode } from 'react';

/** Grey shimmer block used while data loads. */
export function Skeleton({
  width = '100%',
  height = 14,
  radius = 4,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
}): JSX.Element {
  return (
    <span
      className="skeleton"
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

export function SkeletonText({ lines = 3 }: { lines?: number }): JSX.Element {
  return (
    <div className="skeleton-stack" aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} width={index === lines - 1 ? '60%' : '100%'} />
      ))}
    </div>
  );
}

/** Table-shaped loading state so the layout does not jump when rows arrive. */
export function TableSkeleton({
  rows = 6,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}): JSX.Element {
  return (
    <div className="table-wrap" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">Loading…</span>
      <table className="data-table">
        <tbody>
          {Array.from({ length: rows }, (_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }, (_, columnIndex) => (
                <td key={columnIndex}>
                  <Skeleton width={columnIndex === 0 ? '70%' : '45%'} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CardSkeleton({ count = 4 }: { count?: number }): JSX.Element {
  return (
    <div className="stat-grid" aria-busy="true">
      {Array.from({ length: count }, (_, index) => (
        <div className="stat-tile" key={index}>
          <Skeleton width="55%" height={11} />
          <Skeleton width="40%" height={26} />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon = '∅',
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: string;
}): JSX.Element {
  return (
    <div className="empty-state">
      <div className="empty-icon" aria-hidden="true">
        {icon}
      </div>
      <p className="empty-title">{title}</p>
      {description ? <p className="empty-description">{description}</p> : null}
      {action ? <div className="empty-action">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  message,
  detail,
  onRetry,
}: {
  message: string;
  detail?: string | null;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div className="error-state" role="alert">
      <div className="error-icon" aria-hidden="true">
        !
      </div>
      <div className="error-body">
        <p className="error-title">{message}</p>
        {detail ? <p className="error-detail">{detail}</p> : null}
      </div>
      {onRetry ? (
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function InlineError({ message }: { message: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <p className="inline-error" role="alert">
      {message}
    </p>
  );
}

export function Spinner({ label = 'Working…' }: { label?: string }): JSX.Element {
  return (
    <span className="spinner" role="status" aria-label={label}>
      <span className="spinner-ring" aria-hidden="true" />
    </span>
  );
}

export function ProgressBar({
  value,
  label,
  tone = 'info',
}: {
  value: number;
  label?: string;
  tone?: 'info' | 'good' | 'bad';
}): JSX.Element {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      className={`progress progress-${tone}`}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? 'Progress'}
    >
      <div className="progress-fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}
